import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from '../i18n/LanguageContext';
import { useThemeColor } from '../hooks/useThemeColor';
import { useOrganizationLogo } from '../hooks/useOrganizationLogo';
import { fetchLoginBranding } from '../slice/themeSlice';
import { BASE_AUTH_URL } from '../components/constants';

// Custom org logo when set, otherwise the default AI Spaces logo (useOrganizationLogo already falls back).
const LogoBadge = ({ logoUrl }) => (
  <div className="flex items-center justify-center mb-6">
    <img src={logoUrl} alt="Logo" className="h-20 w-auto max-w-[280px] object-contain" />
  </div>
);

const EyeIcon = ({ visible }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.6 18.6 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" />
    )}
  </svg>
);

const Spinner = ({ color }) => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none" style={{ color: color || '#ffffff' }}>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// Standard field — matches the app's own form fields (Users.jsx inputCls).
const FloatingField = ({ label, trailing, inputRef, primaryColor, onFocus, onBlur, ...props }) => (
  <div>
    {label && <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{label}</label>}
    <div className="relative">
      <input
        ref={inputRef}
        {...props}
        onFocus={(e) => { e.target.style.borderColor = primaryColor || '#6D28D9'; onFocus?.(e); }}
        onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; onBlur?.(e); }}
        style={{ borderColor: '#E5E7EB' }}
        className="w-full border rounded-lg px-4 py-3 text-[14px] text-gray-900 outline-none transition-colors disabled:bg-gray-50"
      />
      {trailing && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">{trailing}</div>
      )}
    </div>
  </div>
);

const ResetPassword = () => {
  const { t } = useTranslation();
  const { primaryColor } = useThemeColor();
  const { logoUrl } = useOrganizationLogo();
  const dispatch = useDispatch();

  const [token, setToken] = useState('');
  const [pageState, setPageState] = useState('form'); // 'form' | 'error'
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (!urlToken) {
      setError(t('auth.genericError'));
      setPageState('error');
      return;
    }
    setToken(urlToken);

    // Decode the token's payload (not verified — display purposes only) to
    // fetch the organization's branding for this reset link's recipient.
    try {
      const payload = JSON.parse(atob(urlToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.sub) dispatch(fetchLoginBranding(payload.sub));
    } catch {
      // Ignore — falls back to default branding.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError(t('auth.passwordTooShort')); return; }
    if (newPassword !== confirmPassword) { setError(t('auth.passwordMismatch')); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_AUTH_URL}/reset-password/confirm`, { token, new_password: newPassword });
      const data = res.data;
      if (data.needs_mfa_setup) {
        window.location.replace(
          `/login?email=${encodeURIComponent(data.email)}&mfa_setup_token=${encodeURIComponent(data.mfa_setup_token)}`,
        );
        return;
      }
      if (data.needs_mfa) {
        window.location.replace(
          `/login?email=${encodeURIComponent(data.email)}&mfa_token=${encodeURIComponent(data.mfa_token)}`,
        );
        return;
      }
      localStorage.setItem('token', data.token);
      localStorage.setItem('email', data.email);
      localStorage.setItem('name', data.name);
      localStorage.setItem('id', data.id);
      localStorage.setItem('language', 'de');
      window.location.replace('/');
    } catch (err) {
      setError(err?.response?.data?.detail || t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  };

  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2] p-6">
        <div className="bg-white rounded-[24px] shadow-[0_4px_48px_rgba(0,0,0,0.10)] w-full max-w-[440px] px-10 py-10 text-center">
          <p className="text-[15px] text-red-600">{error || t('auth.genericError')}</p>
          <a href="/login" className="inline-block mt-6 text-[14px] font-medium" style={{ color: primaryColor }}>
            {t('auth.backToLogin')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F2F2F2] p-6">
      <div className="bg-white rounded-[24px] shadow-[0_4px_48px_rgba(0,0,0,0.10)] w-full max-w-[440px] px-10 py-10">
        <form onSubmit={handleSubmit}>
          <LogoBadge logoUrl={logoUrl} />
          <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">
            {t('auth.setNewPasswordTitle')}
          </h1>
          <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
            {t('auth.setNewPasswordSubtitle')}
          </p>

          {error && (
            <div className="mb-5 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <FloatingField primaryColor={primaryColor}
              label={t('auth.newPasswordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
              trailing={(
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="pointer-events-auto">
                  <EyeIcon visible={showPassword} />
                </button>
              )}
            />
            <FloatingField primaryColor={primaryColor}
              label={t('auth.confirmPasswordPlaceholder')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              trailing={(
                <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} className="pointer-events-auto">
                  <EyeIcon visible={showConfirmPassword} />
                </button>
              )}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 py-3.5 rounded-xl text-white text-[15px] font-semibold transition hover:brightness-90 active:brightness-75 disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: primaryColor }}
          >
            {loading ? <Spinner /> : t('auth.resetPasswordButton')}
          </button>

          <div className="text-center mt-4">
            <a href="/login" className="text-[14px] font-medium" style={{ color: primaryColor }}>
              {t('auth.backToLogin')}
            </a>
          </div>
        </form>
      </div>
      <div className="flex items-center justify-center gap-6 mt-6 text-[13px] text-gray-500">
        <a href="#" className="hover:text-gray-700 transition">{t('auth.footerPrivacyPolicy')}</a>
        <a href="#" className="hover:text-gray-700 transition">{t('auth.footerTermsOfService')}</a>
        <a href="#" className="hover:text-gray-700 transition">{t('auth.footerSupport')}</a>
      </div>
    </div>
  );
};

export default ResetPassword;
